import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import CountyWorkspace from '../components/CountyWorkspace';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { getSectionConfig, DATA_STATUS_OPTIONS } from '../lib/countySections';

export default function CountyChildFormPage({ section, mode }) {
  const { stateId, countyId, itemId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const cfg = getSectionConfig(section);

  const [data, setData] = useState({ status: 'draft' });
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode !== 'edit') return;
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get(`/admin/states/${stateId}/counties/${countyId}/${cfg.apiPath}/${itemId}`);
        if (!active) return;
        setData(res.data);
        setOriginal(res.data);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [stateId, countyId, itemId, mode, cfg.apiPath]);

  function update(field, value) {
    setData((d) => ({ ...d, [field]: value }));
  }

  function validate() {
    const errs = {};
    cfg.formGroups.forEach((g) => g.fields.forEach((f) => {
      if (f.required && !String(data[f.key] || '').trim()) errs[f.key] = 'Required';
      if (f.type === 'url' && data[f.key] && !/^https?:\/\//i.test(data[f.key])) {
        errs[f.key] = 'Must start with http(s)://';
      }
      if (f.type === 'email' && data[f.key] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data[f.key])) {
        errs[f.key] = 'Invalid email';
      }
    }));
    if (data.source_url && !/^https?:\/\//i.test(data.source_url)) errs.source_url = 'Must start with http(s)://';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!validate()) {
      setError('Please correct the highlighted fields.');
      return;
    }
    setSaving(true);
    try {
      const base = `/admin/states/${stateId}/counties/${countyId}/${cfg.apiPath}`;
      if (mode === 'create') {
        const res = await adminApi.post(base, data);
        toast.success(`${cfg.titleSingular} created`);
        navigate(`/admin/states/${stateId}/counties/${countyId}/${cfg.slug}/${res.data.id}`);
      } else {
        const res = await adminApi.put(`${base}/${itemId}`, data);
        setData(res.data);
        setOriginal(res.data);
        toast.success(`${cfg.titleSingular} updated`);
      }
    } catch (e) {
      const msg = extractError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  function renderField(f) {
    const common = {
      value: data[f.key] || '',
      onChange: (e) => update(f.key, e.target.value),
      isInvalid: !!errors[f.key],
      'data-testid': `${cfg.slug}-field-${f.key}`,
    };
    if (f.type === 'select') {
      return (
        <Form.Select {...common}>
          {(f.options || []).map((opt) => (
            <option key={opt} value={opt}>{opt || '—'}</option>
          ))}
        </Form.Select>
      );
    }
    if (f.type === 'textarea') {
      return <Form.Control as="textarea" rows={f.rows || 3} {...common} />;
    }
    if (f.type === 'date') {
      return <Form.Control type="date" {...common} />;
    }
    if (f.type === 'email') {
      return <Form.Control type="email" {...common} />;
    }
    if (f.type === 'url') {
      return <Form.Control type="url" placeholder="https://…" {...common} />;
    }
    return <Form.Control {...common} />;
  }

  if (loading) {
    return (
      <CountyWorkspace
        stateId={stateId}
        countyId={countyId}
        activeSection={section}
        title={mode === 'create' ? `New ${cfg.titleSingular}` : `Edit ${cfg.titleSingular}`}
        breadcrumbTrail={[{ label: cfg.titlePlural, to: `/admin/states/${stateId}/counties/${countyId}/${cfg.slug}` }, { label: 'Edit' }]}
      >
        <div className="text-center p-5"><Spinner animation="border" /></div>
      </CountyWorkspace>
    );
  }

  return (
    <CountyWorkspace
      stateId={stateId}
      countyId={countyId}
      activeSection={section}
      title={mode === 'create' ? `New ${cfg.titleSingular}` : (original?.[cfg.nameField] || `Edit ${cfg.titleSingular}`)}
      breadcrumbTrail={[
        { label: cfg.titlePlural, to: `/admin/states/${stateId}/counties/${countyId}/${cfg.slug}` },
        { label: mode === 'create' ? 'New' : (original?.[cfg.nameField] || 'Edit') },
      ]}
      actions={
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => navigate(`/admin/states/${stateId}/counties/${countyId}/${cfg.slug}`)}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving} data-testid={`${cfg.slug}-form-save-button`}>
            {saving ? <Spinner size="sm" animation="border" /> : (mode === 'create' ? 'Create' : 'Save changes')}
          </Button>
        </div>
      }
    >
      {error && <Alert variant="danger">{error}</Alert>}
      <Form noValidate onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col xs={12} lg={8}>
            {cfg.formGroups.map((g, gi) => (
              <Card className="mb-3" key={gi}>
                <Card.Header>{g.title}</Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    {g.fields.map((f) => (
                      <Col xs={12} md={f.col || 6} key={f.key}>
                        <Form.Group>
                          <Form.Label>{f.label}</Form.Label>
                          {renderField(f)}
                          {errors[f.key] && <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{errors[f.key]}</Form.Control.Feedback>}
                        </Form.Group>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            ))}
            <Card>
              <Card.Header>Notes</Card.Header>
              <Card.Body>
                <Form.Group>
                  <Form.Control as="textarea" rows={3} value={data.notes || ''} onChange={(e) => update('notes', e.target.value)} placeholder="Editor-only notes (not exposed via public API)" data-testid={`${cfg.slug}-notes-input`} />
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} lg={4}>
            <Card className="mb-3" style={{ position: 'sticky', top: 12 }}>
              <Card.Header>Status & sources</Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select value={data.status || 'draft'} onChange={(e) => update('status', e.target.value)} data-testid={`${cfg.slug}-status-form-select`}>
                    {DATA_STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Form.Select>
                  <Form.Text>Only Published records are returned by the public API.</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Source URL</Form.Label>
                  <Form.Control isInvalid={!!errors.source_url} value={data.source_url || ''} onChange={(e) => update('source_url', e.target.value)} placeholder="https://…" data-testid={`${cfg.slug}-source-url-input`} />
                  <Form.Control.Feedback type="invalid">{errors.source_url}</Form.Control.Feedback>
                  <Form.Text>Where you verified this data.</Form.Text>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Last verified</Form.Label>
                  <Form.Control
                    type="date"
                    value={(data.last_verified_at || '').slice(0, 10)}
                    onChange={(e) => update('last_verified_at', e.target.value)}
                    data-testid={`${cfg.slug}-verified-input`}
                  />
                  <Form.Text>
                    Auto-set to today when this record is applied from an import.
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </CountyWorkspace>
  );
}
